import type { ProductResumeState } from "@redotech/redo-onboarding-api/schema/get-resume-state";
import type { ProductEntry, ProductSetupHighlight } from "../products";

export interface LandingProductSection {
  id: "self-serve" | "guided" | "active";
  label: string;
  products: ProductEntry[];
}

export type LandingProductDestination =
  | { type: "dashboard" }
  | { type: "dashboard-path"; path: string }
  | { type: "onboarding"; path: string };

export function getLandingProductSections(
  products: ProductEntry[],
  enabledProducts: Record<string, boolean> | null,
): LandingProductSection[] {
  const selfServe: ProductEntry[] = [];
  const guided: ProductEntry[] = [];
  const active: ProductEntry[] = [];

  for (const product of products) {
    if (enabledProducts?.[product.id]) {
      active.push(product);
    } else if (product.selfServe || product.externalRedirectPath) {
      selfServe.push(product);
    } else {
      guided.push(product);
    }
  }

  const sections: LandingProductSection[] = [
    { id: "self-serve", label: "Self-serve setup", products: selfServe },
    { id: "guided", label: "Assisted setup", products: guided },
    { id: "active", label: "Active products", products: active },
  ];

  return sections.filter((section) => section.products.length > 0);
}

export function getDefaultLandingProductId(
  sections: LandingProductSection[],
  enabledProducts: Record<string, boolean> | null,
): string | undefined {
  const uiProducts = sections.flatMap((section) => section.products);
  if (uiProducts.length === 0) {
    return undefined;
  }

  if (!enabledProducts?.returns) {
    const returns = uiProducts.find((product) => product.id === "returns");
    if (returns) {
      return returns.id;
    }
  }

  return (
    uiProducts.find((product) => product.id !== "returns")?.id ??
    uiProducts[0]?.id
  );
}

export function getLandingProductHighlights(
  product: ProductEntry,
  catalogProduct: ProductEntry,
): ProductSetupHighlight[] {
  const requiresGuidedSetup =
    !product.selfServe && !product.externalRedirectPath;
  if (requiresGuidedSetup) {
    return [{ label: "Meet with a Redo onboarding specialist" }];
  }

  if (catalogProduct.setupHighlights?.length) {
    return catalogProduct.setupHighlights.slice(0, 4);
  }

  const visibleSteps = catalogProduct.steps.filter((step) => !step.hideFromNav);
  const distinctLabels = Array.from(
    new Set(visibleSteps.map((step) => step.navGroup ?? step.label)),
  );

  if (
    product.externalRedirectPath &&
    distinctLabels.length === 1 &&
    distinctLabels[0] === "Schedule a call"
  ) {
    return [{ label: "Continue setup in the Redo dashboard" }];
  }

  return distinctLabels.slice(0, 4).map((label) => ({ label }));
}

export function getLandingProductSubtext(
  product: Pick<ProductEntry, "subtext">,
  options: {
    isActive: boolean;
    progress?: Pick<ProductResumeState, "completedSteps" | "totalSteps"> | null;
  },
): string {
  if (options.isActive) {
    return "Open in the Redo dashboard";
  }
  if (options.progress && options.progress.totalSteps > 0) {
    return `${options.progress.completedSteps} of ${options.progress.totalSteps} Steps Completed`;
  }
  return product.subtext;
}

export function getLandingProductDestination(
  product: ProductEntry,
  isActive: boolean,
  search: string,
): LandingProductDestination {
  if (isActive) {
    return { type: "dashboard" };
  }
  if (product.externalRedirectPath) {
    return { type: "dashboard-path", path: product.externalRedirectPath };
  }
  return { type: "onboarding", path: `/${product.id}${search}` };
}
